import { FC, useCallback } from 'react';
import { Document } from '@prismicio/client/types/documents';
import classNames from 'classnames';
import { Link } from 'prismic-reactjs';
import { hasLink } from 'utils/prismic';
import { prismicLinkResolver } from '../utils/common';
import { getColor } from '../utils/colors';
import LinkWithQuery from './LinkWithQuery';

export const getPrismicLink = (link, clusters) => {
  if (link?.type === 'clusterpage') {
    const verticalData = clusters?.find((cluster) => cluster.id === link.id);
    const newLink = {
      ...link,
      data: verticalData?.data,
    };
    return Link.url(newLink, prismicLinkResolver);
  }
  return Link.url(link, prismicLinkResolver);
};

interface Props {
  data: Document['data'];
  clusters: Document['data']['clusters'];
}

const HeaderProductsMenu: FC<Props> = ({ data = [], clusters }) => {
  const headerKeys = [...new Set(data.flatMap((cluster) => !cluster.isHeader && cluster.key))].filter((k) => k);
  const headers = data.filter((cluster) => cluster.isHeader && headerKeys.includes(cluster.key));
  const mainLinks = data.filter((cluster) => !headers.map((h) => h.key).includes(cluster.key));
  const productsLinks = data.filter((cluster) => !cluster.isHeader);

  const getColumnClass = useCallback(() => {
    let cols = headers.length;

    if (headers.length) cols += 1;

    switch (cols) {
      case 1:
        return 'md:grid-cols-1';
      case 2:
        return 'md:grid-cols-2';
      case 3:
        return 'md:grid-cols-3';
      case 4:
        return 'md:grid-cols-4';
      case 5:
        return 'md:grid-cols-5';
      case 6:
        return 'md:grid-cols-6';
      default:
        return 'md:grid-cols-1';
    }
  }, [headers]);

  if (!data?.length) return null;

  return (
    <div className="mx-auto grid lg:pl-[3.875rem] max-w-8x5l grid-cols-1 gap-x-8 gap-y-10 px-6 py-10 lg:px-8">
      <nav className={classNames('grid gap-y-6 gap-x-6 md:gap-x-8 md:gap-y-0', getColumnClass())}>
        {!!mainLinks.length && (
          <div>
            <div className="flow-root">
              <ul className="-my-2">
                {mainLinks.map((item) => {
                  const verticalColor = item.color ? getColor(item.color, 600) : null;
                  const pillColor = getColor(item.pillColor, 500);

                  return (
                    <li key={item.title}>
                      <LinkWithQuery
                        key={item.title}
                        href={getPrismicLink(item.link, clusters)}
                        target={item.link?.target ?? '_self'}
                        className="flex gap-x-4 py-2 text-sm font-semibold leading-6 relative"
                      >
                        {
                          verticalColor
                          && <span className={classNames('block w-1 rounded-full flex-none', verticalColor.bg)} />
                        }
                        <span>
                          {item.title}
                          {item.pillLabel && (
                            <span className={classNames(
                              'inline-block rounded-[4px] text-xxs leading-[10px] py-0.5 px-1 opacity-80 ml-1 absolute',
                              pillColor.bg,
                              pillColor.textContrast,
                            )}
                            >
                              {item.pillLabel}
                            </span>
                          )}
                          <small className="block text-xxs leading-3 text-gray-600">{item.description}</small>
                        </span>
                      </LinkWithQuery>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}
        {
          headers.map((header) => {
            const products = productsLinks.filter((p) => p.key === header.key);
            const verticalColor = header.color ? getColor(header.color, 600) : null;
            const pillColor = getColor(header.pillColor, 500);

            return (
              <div key={header.title}>
                <div className="flow-root">
                  <h3 className="flex gap-x-4 text-sm font-medium leading-6 text-gray-500">
                    {
                      verticalColor
                      && <span className={classNames('block w-1 rounded-full flex-none', verticalColor.bg)} />
                    }
                    <span>
                      <LinkWithQuery
                        key={header.title}
                        href={getPrismicLink(header.link, clusters)}
                        className={classNames(
                          'gap-x-4 py-2 text-sm font-semibold leading-6 relative',
                          { 'pointer-events-none': !hasLink(header.link) },
                        )}
                      >
                        {header.title}
                        {header.pillLabel && (
                          <span className={classNames(
                            'inline-block rounded-[4px] text-xxs leading-[10px] py-0.5 px-1 opacity-80 ml-1 absolute',
                            pillColor.bg,
                            pillColor.textContrast,
                          )}
                          >
                            {header.pillLabel}
                          </span>
                        )}
                      </LinkWithQuery>
                      <small className="block text-xxs leading-3 text-gray-600">{header.description}</small>
                    </span>
                  </h3>
                  <div className="mt-6 flow-root">
                    <div className="-my-2 flex flex-col">
                      {products.map((item) => {
                        const productPillColor = getColor(item.pillColor, 500);
                        return (
                          <LinkWithQuery
                            key={item.title}
                            href={getPrismicLink(item.link, clusters)}
                            className="gap-x-4 py-2 text-sm font-semibold leading-6 relative"
                          >
                            {item.title}
                            {item.pillLabel && (
                              <span className={classNames(
                                'inline-block rounded-[4px] text-xxs leading-[10px]',
                                'py-0.5 px-1 opacity-80 ml-1 absolute',
                                productPillColor.bg,
                                productPillColor.textContrast,
                              )}
                              >
                                {item.pillLabel}
                              </span>
                            )}
                          </LinkWithQuery>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        }
      </nav>
    </div>
  );
};
export default HeaderProductsMenu;
